// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// jevViewerModel — the PURE half of the Jev window (2026-10-07).
//
// Ported from PRED's viewer (predictive-scheduling scripts/jev_viewer.template.html),
// which is the spec: sky-view tiles, the three charts' bins, filtering, sorting.
// No React, no clock except where a caller passes one, never mutates its input.
// A row here is a JevShadowLog doc (decoded) plus `_now` (the booking now, from
// the Convex query's activeQueue join).
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

export const BUCKETS = ["1", "2", "3", "4+"];
export const STATUSES = ["ok", "skipped", "timeout", "error"];
// PRED's quoted price for Jev input tokens.
export const USD_PER_MILLION_INPUT_TOKENS = 0.04;

export const daysAnswer = (r) => r?.response?.answers?.days || null;
export const jobOf = (r) => r?.request?.state?.job || {};
export const whenOf = (r) => new Date(r?.at);

/** Jev's probability the job takes more than one working day (answered calls only). */
export function pMulti(r) {
  const d = daysAnswer(r);
  if (!d) return null;
  return 1 - (d.probabilities?.["1"] ?? 0);
}

/** The reference table's P(>1 day), or null. */
export function refPMulti(r) {
  return r?.referenceAnswer ? 1 - (r.referenceAnswer["1"] ?? 0) : null;
}

/** The bucket the reference table rates most likely, or null. */
export function topPick(probabilities) {
  if (!probabilities) return null;
  const ents = Object.entries(probabilities);
  if (!ents.length) return null;
  return ents.reduce((best, cur) => (cur[1] > best[1] ? cur : best))[0];
}

export const pct = (x, d = 0) => (x == null || !Number.isFinite(x) ? "—" : (100 * x).toFixed(d) + "%");

export function median(a) {
  if (!a.length) return null;
  const s = [...a].sort((x, y) => x - y);
  return s[Math.floor(s.length / 2)];
}

export function quantile(a, p) {
  if (!a.length) return null;
  const s = [...a].sort((x, y) => x - y);
  return s[Math.min(s.length - 1, Math.floor(p * s.length))];
}

/** Status + free-text search (Keller ID / floor / office); newest first. */
export function filterRows(rows, { status = "", search = "" } = {}) {
  const s = String(search).trim().toLowerCase();
  return rows
    .filter((r) => !status || r.status === status)
    .filter(
      (r) =>
        !s ||
        [r.kellerId, jobOf(r).floor_type, jobOf(r).office].some((v) =>
          String(v ?? "").toLowerCase().includes(s)
        )
    )
    .sort((a, b) => whenOf(b) - whenOf(a));
}

/** The sky-view tiles: [{ label, value, note }]. `fmtTime` formats a Date. */
export function kpiTiles(rows, fmtTime) {
  const ok = rows.filter((r) => r.status === "ok");
  const lat = ok.map((r) => r.latencyMs).filter(Number.isFinite);
  const tokIn = rows.reduce((n, r) => n + (r.response?.usage?.input_tokens || 0), 0);
  const multi = ok.filter((r) => pMulti(r) >= 0.5).length;
  const agree = ok.filter(
    (r) => r.referenceAnswer && daysAnswer(r)?.choice === topPick(r.referenceAnswer)
  ).length;
  const finished = rows.filter((r) => r._now?.finished).length;
  const count = (s) => rows.filter((r) => r.status === s).length;
  const newest = rows.length ? rows.reduce((a, b) => (whenOf(b) > whenOf(a) ? b : a)) : null;
  const oldest = rows.length ? rows.reduce((a, b) => (whenOf(b) < whenOf(a) ? b : a)) : null;
  return [
    { label: "Calls", value: String(rows.length), note: rows.length ? `${fmtTime(whenOf(oldest))} → ${fmtTime(whenOf(newest))}` : "none yet" },
    { label: "Answered", value: String(ok.length), note: `${pct(ok.length / (rows.length || 1))} of calls` },
    { label: "Timeouts", value: String(count("timeout")), note: "mostly cold starts" },
    { label: "Skipped / errors", value: `${count("skipped")} / ${count("error")}`, note: "skip = no labour $ or unknown floor" },
    { label: "Median latency", value: lat.length ? `${median(lat)} ms` : "—", note: lat.length ? `90th pct ${quantile(lat, 0.9)} ms` : "" },
    { label: "Jev says > 1 day", value: String(multi), note: ok.length ? `${pct(multi / ok.length)} of answered · FAV booked 1` : "" },
    { label: "Jev = table's top pick", value: ok.length ? pct(agree / ok.length) : "—", note: "agreement with the no-AI reference" },
    { label: "Jobs finished so far", value: String(finished), note: "gradable once finished" },
    {
      label: "Input tokens",
      value: tokIn.toLocaleString("en-US"),
      note: `≈ $${((tokIn * USD_PER_MILLION_INPUT_TOKENS) / 1e6).toFixed(4)} at the quoted $${USD_PER_MILLION_INPUT_TOKENS}/M`,
    },
  ];
}

/** Calls over time: hourly under 3 days of span, else daily. Bins start on LOCAL
 *  hour / midnight boundaries so a bar's label matches the calls inside it
 *  (#596 review 3 — UTC-midnight days put Central mornings under the day before).
 *  `labelFor(date, hourly)`. */
export function timeBins(rows, labelFor, fmtTime) {
  if (!rows.length) return [];
  const ts = rows.map((r) => +whenOf(r));
  const lo = Math.min(...ts);
  const hi = Math.max(...ts);
  const hourly = hi - lo < 3 * 864e5;
  const first = new Date(lo);
  if (hourly) first.setMinutes(0, 0, 0);
  else first.setHours(0, 0, 0, 0);
  const next = (d) => {
    const n = new Date(d);
    if (hourly) n.setHours(n.getHours() + 1);
    else n.setDate(n.getDate() + 1);
    return n;
  };
  const bins = [];
  for (let t0 = first; +t0 <= hi && bins.length < 400; t0 = next(t0)) {
    const t1 = next(t0);
    const inb = rows.filter((r) => +whenOf(r) >= +t0 && +whenOf(r) < +t1);
    bins.push({
      label: labelFor(t0, hourly),
      value: inb.length,
      tip: `${fmtTime(t0)}: ${inb.length} calls (${inb.filter((r) => r.status === "ok").length} ok)`,
    });
  }
  return bins;
}

/** Which 10% bin (0..9) a probability falls in. Rounded first: 1 − 0.9 is
 *  0.0999… in floating point and would land one bin low (#596 review 3). */
export function pBin(p) {
  return Math.min(9, Math.max(0, Math.floor(Math.round(p * 1e6) / 1e5)));
}

/** Histogram of Jev's P(>1 day) in 10% bins (answered calls). */
export function pMultiBins(rows) {
  const counts = Array(10).fill(0);
  for (const r of rows) {
    if (r.status !== "ok") continue;
    const p = pMulti(r);
    if (p != null && Number.isFinite(p)) counts[pBin(p)]++;
  }
  return counts.map((n, i) => ({ label: `${i * 10}%`, value: n, tip: `Jev said ${i * 10}–${i * 10 + 10}%: ${n} jobs` }));
}

export const LATENCY_EDGES = [0, 250, 500, 750, 1000, 1250, 1500, 2000, 2500, Infinity];

/** Latency histogram (job read + Jev), every call with a latency. */
export function latencyBins(rows) {
  return LATENCY_EDGES.slice(0, -1).map((e, i) => {
    const hiEdge = LATENCY_EDGES[i + 1];
    const n = rows.filter((r) => r.latencyMs >= e && r.latencyMs < hiEdge).length;
    return {
      label: hiEdge === Infinity ? `${e}+` : `${e}`,
      value: n,
      tip: `${e}–${hiEdge === Infinity ? "∞" : hiEdge} ms: ${n} calls`,
    };
  });
}

/** Sort keys for the call table; unknown key → time. Returns a NEW array. */
export const SORT_KEYS = {
  at: (r) => +whenOf(r),
  kellerId: (r) => String(r.kellerId ?? ""),
  status: (r) => String(r.status ?? ""),
  floor: (r) => String(jobOf(r).floor_type ?? ""),
  labour: (r) => jobOf(r).labour_dollars ?? -1,
  choice: (r) => String(daysAnswer(r)?.choice ?? ""),
  pmulti: (r) => pMulti(r) ?? -1,
  ref: (r) => refPMulti(r) ?? -1,
  now: (r) => r._now?.bookedWorkingDays ?? -1,
  latencyMs: (r) => r.latencyMs ?? -1,
  actual: (r) => (r._grade?.final ? r._grade.actual?.workingDays ?? -1 : -1),
};

export function sortRows(rows, key, dir) {
  const k = SORT_KEYS[key] || SORT_KEYS.at;
  return [...rows].sort((a, b) => (k(a) > k(b) ? 1 : k(a) < k(b) ? -1 : 0) * (dir > 0 ? 1 : -1));
}

/** Deep link: "#detail" → newest row; "#<KellerID>" → that job's newest row. */
export function rowForHash(rows, hash) {
  const h = decodeURIComponent(String(hash ?? "").replace(/^#/, ""));
  if (!h) return null;
  const sorted = [...rows].sort((a, b) => whenOf(b) - whenOf(a));
  return h === "detail" ? sorted[0] || null : sorted.find((r) => r.kellerId === h) || null;
}

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// Grades (PRED-013): a row's `_grade` is its JevAnswerGrades doc, or null.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

/** The "Actual" cell: pending until the job finishes, then its working days and
 *  whether Jev's day choice was right. */
export function gradeCell(grade) {
  if (!grade) return { text: "—", hit: null };
  // Same cancelled set as the trigger + summary (20 Cancelled, 21 Write Off).
  if (!grade.final) return { text: [20, 21].includes(Number(grade.jobStatus)) ? "cancelled" : "pending", hit: null };
  const n = grade.actual?.workingDays;
  const hit = typeof grade.days?.hit === "boolean" ? grade.days.hit : null;
  return { text: n != null ? `${n}d` : "?", hit };
}

/** One plain line about the crew ranking vs the crew actually assigned, or null. */
export function crewLine(grade) {
  const c = grade?.crew;
  if (!c) return null;
  if (!c.assigned) return `No crew assigned yet · the ranking's top choice: ${c.topChoice ?? "—"}`;
  return c.wasAmongFree
    ? `Assigned crew ${c.assigned} was ranked #${c.rankOfAssigned} of ${c.ofFree} (the ranking's top choice: ${c.topChoice ?? "—"})`
    : `Assigned crew ${c.assigned} was not among the ${c.ofFree} free crews (the ranking's top choice: ${c.topChoice ?? "—"})`;
}

/** Accuracy tiles from the gradeSummary action (undefined = still computing). */
export function gradeTiles(summary, includeReplays) {
  if (summary === undefined) return [{ label: "Graded", value: "…", note: "computing accuracy" }];
  if (!summary) return [{ label: "Graded", value: "—", note: "accuracy unavailable" }];
  const sets = [["live", "Live"]].concat(includeReplays ? [["replay", "Replays"]] : []);
  const tiles = [];
  for (const [k, name] of sets) {
    const s = summary[k];
    if (!s) continue;
    tiles.push(
      {
        label: `Graded (${name})`,
        value: String(s.final),
        note: `${s.pending} pending · final once the job finishes${s.cancelled ? ` · ${s.cancelled} cancelled` : ""}`,
      },
      { label: `Jev hit rate (${name})`, value: pct(s.hitRate), note: "day choice = actual working days" },
      {
        label: `Overruns caught (${name})`,
        value: s.overruns ? `${s.overrunsCaughtTop10}/${s.overruns}` : "—",
        note: s.overruns ? `${pct(s.overrunsCaughtTop10Rate)} in Jev's top 10% of P(>1 day)` : "no overruns graded yet",
      },
      {
        label: `Assigned crew = top pick (${name})`,
        value: pct(s.crewTopRate),
        note: s.crewGraded ? `${s.crewGraded} graded crew rankings · ${pct(s.crewAmongFreeRate)} among the free crews` : "no crew rankings graded yet",
      }
    );
  }
  return tiles;
}

/** "What Jev knows" (PRED-016 part 4): one row per REBUILD — its similar-jobs table and
 *  crew model share the rebuild's createdAt stamp. Newest first. A table with no crew
 *  model (or the reverse) still gets a row, with the missing side null. */
export function pairVersions(versions) {
  const rows = [];
  const byTime = [...(versions || [])].sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
  for (const v of byTime) {
    const near = rows.find((r) => r.createdAt !== null && v.createdAt !== null && Math.abs(r.createdAt - v.createdAt) < 60 * 1000 && !r[v.kind === "similarJobs" ? "ref" : "crew"]);
    const row = near || { createdAt: v.createdAt ?? null, ref: null, crew: null };
    if (v.kind === "similarJobs") row.ref = v;
    else if (v.kind === "crewModel") row.crew = v;
    else continue;
    if (!near) rows.push(row);
  }
  return rows.map((r) => {
    const lead = r.ref || r.crew;
    return {
      ...r,
      status: lead.status,
      asOf: lead.asOf,
      // Usable as a rollback / approve target only as a complete pair, neither current.
      canUse: Boolean(r.ref && r.crew && r.ref.status !== "current" && r.crew.status !== "current" && r.ref.status === r.crew.status),
    };
  });
}

// - - - - - - - - - - shadow v6 / v7 (PRED-017 / -021), log-only
const NEEDS_PERSON_REASONS = {
  similar_jobs_disagree: "the similar past jobs disagree about how long it takes",
  jev_says_unusual: "Jev says the job is unusual",
};
/** The "needs a person" flag in words, or null when not flagged. */
export function needsPersonText(np) {
  if (!np || !np.flag) return null;
  const why = (np.reasons || []).map((r) => NEEDS_PERSON_REASONS[r] || String(r).replace(/_/g, " "));
  return why.length ? `Worth a person's check: ${why.join(", and ")}.` : "Worth a person's check.";
}
/** Where the square feet came from, in words. */
export function squareFeetSourceText(src) {
  if (src === "materials") return "the job's materials";
  if (src === "estimated_from_labour_items") return "estimated from the labour line items (no materials yet)";
  return src ? String(src) : null;
}
/** v7: one row per date FAV could book, FAV's pick and the best date marked. */
export function datePlanRows(plan) {
  if (!plan || plan.skipReason || !Array.isArray(plan.perDate)) return [];
  const bestDate = plan.bestOverall ? plan.bestOverall.date : null;
  return plan.perDate.map((d) => ({
    date: d.date,
    qualified: d.qualifiedCrews,
    free: d.freeCrews,
    unassigned: d.unassigned,
    bestCrew: d.best ? d.best.payrollNo : null,
    bestOneDay: d.best && Number.isFinite(d.best.oneDay) ? d.best.oneDay : null,
    bestFlags: d.best && Array.isArray(d.best.flags) ? d.best.flags : [],
    isFav: d.date === plan.favDate,
    isBest: d.date === bestDate,
  }));
}

// - - - - - - - - - - FAV 2.0 advice (#642, PRED-028), log-only
// The Lambda logs a `fav2` block on every FAV reserve call since 2026-10-13: the crew,
// start, end and working days FAV 2.0 WOULD book. Advice only — FAV still books v1.

/** The call-table cell: "1d · 1795.01 · 10/26/2026" / "2d · … · 11/12/2026–11/13/2026" / "skipped · <reason>". */
export function fav2Cell(fav2) {
  if (!fav2) return "—";
  if (fav2.status !== "ok") return `skipped · ${fav2.reason || "?"}`;
  const when = fav2.endDate && fav2.endDate !== fav2.startDate ? `${fav2.startDate}–${fav2.endDate}` : fav2.startDate;
  return `${fav2.workingDays}d · ${fav2.installer} · ${when}`;
}

/** Sky-view tiles over the calls that carry a fav2 block (older calls don't). */
export function fav2Tiles(rows) {
  const withAdvice = rows.filter((r) => r.fav2);
  const ok = withAdvice.filter((r) => r.fav2.status === "ok");
  return [
    {
      label: "FAV 2.0 found a crew",
      value: withAdvice.length ? `${ok.length} of ${withAdvice.length}` : "—",
      note: withAdvice.length ? `${pct(ok.length / withAdvice.length)} of calls with FAV 2.0 advice` : "no calls with FAV 2.0 advice yet",
    },
    { label: "FAV 2.0 > 1 day", value: String(ok.filter((r) => r.fav2.workingDays > 1).length), note: "FAV booked 1" },
    { label: "FAV 2.0 moved the start", value: String(ok.filter((r) => r.fav2.sameStartAsV1 === false).length), note: "a different day from FAV's" },
  ];
}
